import { type CSSProperties, Fragment, type ReactNode } from 'react';
import type { Cell, RowData } from '@tanstack/react-table';
import classNames from 'classnames';

import SkeletonBox from '@/components/atoms/SkeletonBox/SkeletonBox';
import EmptyState from '@/components/molecules/EmptyState';

import { ColumnTemplate } from './ColumnTemplate';
import type { DataTableFeatures, DataTableInstance, DataTableRow } from './features';
import type { DataTableRowProps } from './types';

import styles from './GridLayout.module.scss';

const SKELETON_ROW_MARGIN = 32;

interface DataTableBodyProps<T extends RowData> {
    table: DataTableInstance<T>;
    getRowProps?: (row: DataTableRow<T>) => DataTableRowProps;
    /** The native layout sizes each body cell from its column's `meta.width`. */
    cellWidths: boolean;
    renderExpanded?: (row: DataTableRow<T>) => ReactNode;
    emptyState?: ReactNode;
    isLoading: boolean;
    skeletonHeight: number;
}

/** Body cells, with any `meta.colSpan` swallowing the cells it covers. */
function visibleCells<T extends RowData>(
    row: DataTableRow<T>
): { cell: Cell<DataTableFeatures, T>; colSpan?: number }[] {
    const cells = row.getVisibleCells();
    const out: { cell: Cell<DataTableFeatures, T>; colSpan?: number }[] = [];
    let skip = 0;
    for (const cell of cells) {
        if (skip > 0) {
            skip -= 1;
            continue;
        }
        const colSpan = cell.column.columnDef.meta?.colSpan?.(row);
        if (colSpan !== undefined && colSpan > 1) skip = colSpan - 1;
        out.push({ cell, colSpan });
    }
    return out;
}

function cellStyle(
    colSpan: number | undefined,
    width: string | undefined
): CSSProperties | undefined {
    // The grid layout ignores colSpan; a span of grid tracks does the same there.
    if (colSpan) return { gridColumn: `span ${colSpan}` };
    return width ? { width } : undefined;
}

function BodyCell<T extends RowData>(props: {
    cell: Cell<DataTableFeatures, T>;
    colSpan?: number;
    cellWidths: boolean;
}) {
    const { cell, colSpan, cellWidths } = props;
    const meta = cell.column.columnDef.meta;
    const row = cell.row;
    const cellClassName =
        typeof meta?.cellClassName === 'function' ? meta.cellClassName(row) : meta?.cellClassName;
    const content = (
        <ColumnTemplate template={cell.column.columnDef.cell} context={cell.getContext()} />
    );
    const Tag = meta?.rowHeader ? 'th' : 'td';

    return (
        <Tag
            scope={meta?.rowHeader ? 'row' : undefined}
            data-name={cell.column.id}
            data-align={meta?.cellAlign?.(row) ?? meta?.align ?? 'left'}
            colSpan={colSpan}
            style={cellStyle(colSpan, cellWidths ? meta?.width : undefined)}
            title={meta?.cellTitle?.(row)}
            className={classNames(cellClassName, meta?.truncate && styles.cellTruncate)}>
            {meta?.truncate ? <span className={styles.cellText}>{content}</span> : content}
        </Tag>
    );
}

function rowStyle(props: DataTableRowProps): CSSProperties | undefined {
    const style: Record<string, string> = {};
    if (props.rowHeight !== undefined) style.height = `${props.rowHeight}px`;
    if (props.gridTemplate !== undefined) style['--grid-table-columns'] = props.gridTemplate;
    return Object.keys(style).length > 0 ? style : undefined;
}

/** A row that spans every column, for the skeleton, the empty state and expanded detail. */
function FullWidthRow(props: { span: number; className: string; children: ReactNode }) {
    const { span, className, children } = props;
    return (
        <tr className={className}>
            <td colSpan={span}>{children}</td>
        </tr>
    );
}

export default function DataTableBody<T extends RowData>(props: DataTableBodyProps<T>) {
    const {
        table,
        getRowProps,
        cellWidths,
        renderExpanded,
        emptyState,
        isLoading,
        skeletonHeight,
    } = props;
    const span = table.getVisibleLeafColumns().length;
    const rows = table.getRowModel().rows;

    if (isLoading) {
        return (
            <tbody>
                <tr
                    className={classNames(styles.fullWidthRow, styles.noHover)}
                    style={{ height: skeletonHeight + SKELETON_ROW_MARGIN }}>
                    <td colSpan={span}>
                        <SkeletonBox width="100%" height={skeletonHeight} borderRadius={8} />
                    </td>
                </tr>
            </tbody>
        );
    }

    if (rows.length === 0 && emptyState !== undefined) {
        return (
            <tbody>
                <FullWidthRow
                    span={span}
                    className={classNames(styles.fullWidthRow, styles.noHover, styles.emptyRow)}>
                    {typeof emptyState === 'string' ? (
                        <EmptyState title={emptyState} appearance="quiet" />
                    ) : (
                        emptyState
                    )}
                </FullWidthRow>
            </tbody>
        );
    }

    return (
        <tbody>
            {rows.map((row) => {
                const rowProps = getRowProps?.(row) ?? {};
                const variant = rowProps.variant ?? 'default';
                return (
                    <Fragment key={row.id}>
                        <tr
                            className={classNames(rowProps.className, {
                                [styles.noHover]: rowProps.disableHover,
                            })}
                            data-variant={variant === 'default' ? undefined : variant}
                            data-depth={row.depth > 0 ? row.depth : undefined}
                            style={rowStyle(rowProps)}
                            onClick={rowProps.onClick}>
                            {visibleCells(row).map(({ cell, colSpan }) => (
                                <BodyCell
                                    key={cell.id}
                                    cell={cell}
                                    colSpan={colSpan}
                                    cellWidths={cellWidths}
                                />
                            ))}
                        </tr>
                        {renderExpanded && row.getIsExpanded() && (
                            <FullWidthRow span={span} className={styles.expandedRow}>
                                {renderExpanded(row)}
                            </FullWidthRow>
                        )}
                    </Fragment>
                );
            })}
        </tbody>
    );
}
